import { DATA_WAREHOUSE_PAGE_TEXT, DATA_WAREHOUSE_URL } from 'modules/dataWarehouse';
import { HeaderNavIcons } from 'ui/icons/headerNavigation/constants';
import { PROJECT_PERMISSIONS } from 'utils/permissions';
import { NAVBAR_PROJECT_SETTINGS, NavbarDropdownGroup, NavbarVariant } from '../types';

/**
 * Copied from `src/core/navbar/constants` with some modifications
 * - replace icon: string with Icon: ReactNode
 */

const MESSAGING_DROPDOWN: NavbarDropdownGroup = {
  header: 'Messaging',
  dropdownOptions: [
    {
      href: '/workflows',
      icon: HeaderNavIcons.JOURNEYS,
      pageDescription: 'Build automated, multi-channel customer journeys',
      rbacPermission: PROJECT_PERMISSIONS.ViewMessaging,
      title: 'Journeys',
    },
    {
      href: '/campaigns/manage',
      icon: HeaderNavIcons.CAMPAIGNS,
      pageDescription: 'Draft, schedule, and send messages',
      rbacPermission: PROJECT_PERMISSIONS.ViewMessaging,
      title: 'Campaigns',
    },
    {
      href: '/experiments',
      icon: HeaderNavIcons.EXPERIMENTS,
      pageDescription: 'Create and test multiple versions of a campaign',
      rbacPermission: PROJECT_PERMISSIONS.ViewMessaging,
      title: 'Experiments',
    },
  ],
};

const CONTENT_DROPDOWN: NavbarDropdownGroup = {
  header: 'Content',
  dropdownOptions: [
    {
      href: '/templates',
      icon: HeaderNavIcons.TEMPLATES,
      pageDescription: 'Design reusable message layouts and content',
      title: 'Templates',
    },
    {
      href: '/templates/snippets',
      icon: HeaderNavIcons.SNIPPETS,
      pageDescription: 'Create reusable content blocks for your templates and campaigns',
      title: 'Snippets',
    },
    {
      featureFlags: ['MetadataStoreUI'],
      href: '/catalogs',
      icon: HeaderNavIcons.CATALOGS,
      pageDescription: 'Store information about your products and services in Iterable',
      rbacPermission: PROJECT_PERMISSIONS.ViewCatalogs,
      title: 'Catalogs',
    },
    {
      href: '/integrations/dataFeeds',
      icon: HeaderNavIcons.DATA_FEEDS,
      pageDescription: 'Fetch user data from outside Iterable',
      rbacPermission: PROJECT_PERMISSIONS.ManageIntegrations,
      title: 'Data Feeds',
    },
    {
      href: '/creativeLibrary',
      icon: HeaderNavIcons.CREATIVE_LIBRARY,
      featureFlags: ['AssetManagerIndex'],
      pageDescription: 'Upload and manage images to use in your campaigns',
      title: 'Creative Library',
    },
  ],
};

const AUDIENCE_DROPDOWN: NavbarDropdownGroup = {
  header: 'Audience',
  dropdownOptions: [
    {
      href: '/segmentation',
      icon: HeaderNavIcons.SEGMENTATION,
      pageDescription: 'Identify groups of users with similar interests and behavior',
      title: 'Segmentation',
      // NOTE: segmentation isn't actual an "external" link, but
      // we need to do a hard refresh for this route because the route requires
      // a window variable to be set when the page loads, and this happens on the backendside.
      // once that functionality is fixed, this can be removed
      isExternal: true,
    },
    {
      href: '/lists',
      icon: HeaderNavIcons.LISTS,
      pageDescription: 'Create and manage your audience lists',
      title: 'Lists',
    },
    {
      href: '/users/lookup',
      icon: HeaderNavIcons.CONTACT_LOOKUP,
      pageDescription: 'View or update a specific user’s profile',
      rbacPermission: PROJECT_PERMISSIONS.ViewAndExportUserData,
      title: 'User Lookup',
    },
    {
      href: '/users/unknownUserActivation',
      icon: HeaderNavIcons.ANONYMOUS_USERS,
      pageDescription: 'Manage which unknown users are added to your project',
      rbacPermission: PROJECT_PERMISSIONS.ManageSegmentation,
      featureFlags: ['AnonymousUserCriteria'],
      title: 'Unknown User Activation',
      projectSettings: [NAVBAR_PROJECT_SETTINGS.enableAnonUserTracking],
    },
  ],
};

const INSIGHTS_DROPDOWN: NavbarDropdownGroup = {
  header: 'Insights',
  dropdownOptions: [
    {
      href: '/messagingInsights',
      icon: HeaderNavIcons.MESSAGING_INSIGHTS,
      linksSubsection: false,
      links: [
        {
          label: 'Saved views',
          href: '/savedViews',
          rbacPermission: PROJECT_PERMISSIONS.ViewReports,
        },
        {
          label: 'Campaign analytics',
          href: '/analytics/campaignPerformance',
          rbacPermission: PROJECT_PERMISSIONS.ViewReports,
        },
      ],
      pageDescription: 'See how your recent campaigns are performing',
      rbacPermission: PROJECT_PERMISSIONS.ViewReports,
      title: 'Messaging Insights',
    },
    {
      title: 'Intelligent Insights',
      href: '/intelligentInsights/brandAffinityInsights',
      icon: HeaderNavIcons.INTELLIGENT_INSIGHTS,
      pageDescription: 'Understand how Iterable AI is optimizing your marketing',
      rbacPermission: PROJECT_PERMISSIONS.ViewReports,
      featureFlags: ['BrandAffinity'],
      links: [
        {
          label: 'brandAffinityInsights',
          href: '/intelligentInsights/brandAffinityInsights',
          rbacPermission: PROJECT_PERMISSIONS.ViewReports,
        },
        {
          label: 'froReporting',
          href: '/intelligentInsights/froReporting',
          rbacPermission: PROJECT_PERMISSIONS.ViewReports,
        },
      ],
    },
    {
      title: 'Goals',
      href: '/goals',
      icon: HeaderNavIcons.GOALS,
      pageDescription: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit',
      featureFlags: ['MissionControlHomepage', 'MissionControlBeta'],
    },
    {
      href: '/dashboard',
      icon: HeaderNavIcons.DASHBOARD,
      pageDescription: 'Monitor key marketing performance metrics',
      title: 'Dashboard',
    },
    {
      title: 'Predictive Goals',
      href: '/predictive-audiences',
      icon: HeaderNavIcons.PREDICTIVE_GOALS,
      pageDescription: 'Create predictive metrics for your business goals and improve conversion',
      rbacPermission: PROJECT_PERMISSIONS.ViewGoals,
      featureFlags: ['PredictiveGoals'],
    },
    {
      href: '/reports',
      icon: HeaderNavIcons.REPORTS,
      pageDescription: 'Create and download custom performance reports',
      rbacPermission: PROJECT_PERMISSIONS.ViewReports,
      title: 'Reports',
    },
    {
      icon: HeaderNavIcons.LOGS,
      links: [
        {
          href: '/events',
          label: 'Events',
          rbacPermission: PROJECT_PERMISSIONS.ViewAndExportUserData,
        },
        {
          href: '/purchases',
          label: 'Purchases',
          rbacPermission: PROJECT_PERMISSIONS.ViewAndExportUserData,
        },
        {
          href: '/users/inboundSMS',
          label: 'Inbound SMS',
          rbacPermission: PROJECT_PERMISSIONS.ViewAndExportUserData,
        },
        {
          label: 'Send Calendar',
          href: '/campaigns/calendar',
          // NOTE: the campaigns calendar isn't actual an "external" link, but
          // we need to do a hard refresh for this route because the route requires
          // something to function that only occurs when the backend route is loaded.
          // once that functionality is fixed, this can be removed
          isExternal: true,
        },
        {
          label: 'Scheduled Messages',
          href: '/triggered/scheduledMessages',
          rbacPermission: [
            PROJECT_PERMISSIONS.ViewMessaging,
            PROJECT_PERMISSIONS.ViewAndExportUserData,
          ],
        },
        {
          href: '/campaigns/triggeredSendLog',
          label: 'Sent Messages',
          rbacPermission: [
            PROJECT_PERMISSIONS.ViewMessaging,
            PROJECT_PERMISSIONS.ViewAndExportUserData,
          ],
          // NOTE: triggeredSendLog isn't actual an "external" link, but
          // we need to do a hard refresh for this route because the route requires
          // something to function that only occurs when the backend route is loaded.
          // once that functionality is fixed, this can be removed
          isExternal: true,
        },
        {
          href: '/whatsAppMessages',
          label: 'WhatsApp Messages',
          rbacPermission: [
            PROJECT_PERMISSIONS.ViewMessaging,
            PROJECT_PERMISSIONS.ViewAndExportUserData,
          ],
          isExternal: true,
          featureFlags: ['WhatsAppMessageChannel', 'WhatsAppMessageChannelGA'],
          requireOnlyOneFlag: true,
        },
      ],
      linksSubsection: true,
      title: 'Logs',
    },
  ],
};

const INTEGRATIONS_DROPDOWN: NavbarDropdownGroup = {
  header: 'Integrations',
  simpleLinks: true,
  dropdownOptions: [
    {
      title: 'Data Sync',
      href: '/integrations/dataSync',
      rbacPermission: PROJECT_PERMISSIONS.ManageIntegrations,
      featureFlags: ['EtlIntegration'],
    },
    {
      title: DATA_WAREHOUSE_PAGE_TEXT.pageTitle,
      href: DATA_WAREHOUSE_URL,
      rbacPermission: PROJECT_PERMISSIONS.ManageIntegrations,
      featureFlags: ['HightouchIntegration'],
    },
    {
      title: 'Third-Party',
      href: '/integrations/thirdParty',
      rbacPermission: PROJECT_PERMISSIONS.ManageIntegrations,
    },
    {
      title: 'Facebook',
      href: '/integrations/facebookIntegration',
      rbacPermission: PROJECT_PERMISSIONS.ManageIntegrations,
    },
    {
      title: 'Movable Ink',
      href: '/integrations/movableInkIntegration',
      featureFlags: ['MovableInk'],
      rbacPermission: PROJECT_PERMISSIONS.ManageIntegrations,
    },
    {
      title: 'API Keys',
      href: '/settings/apiKeys',
      rbacPermission: PROJECT_PERMISSIONS.ManageIntegrations,
    },
    {
      title: 'System Webhooks',
      href: '/integrations/webhooks',
      rbacPermission: PROJECT_PERMISSIONS.ManageIntegrations,
    },
    {
      title: 'Journey Webhooks',
      href: '/workflows/webhooks',
      rbacPermission: PROJECT_PERMISSIONS.ManageIntegrations,
    },
  ],
};

export const NAVIGATION_ITEMS = [
  MESSAGING_DROPDOWN,
  CONTENT_DROPDOWN,
  AUDIENCE_DROPDOWN,
  INSIGHTS_DROPDOWN,
  INTEGRATIONS_DROPDOWN,
];

export const ACTIVE_CLASS_NAME = 'is-active';
export const ACTIVE_LOGO_CLASS_NAME = 'is-logo-active';

export const NAVBAR_MENU_IDS = {
  chat: 'chat',
  support: 'support',
  settings: 'settings',
  notifications: 'notifications',
  account: 'account',
  quickActions: 'quickActions',
};

export const NAVBAR_MENU_TOOLTIPS = {
  chat: 'Support Chat',
  support: 'Help',
  settings: 'Settings',
  notifications: 'Notifications',
  account: 'Account and Projects',
  quickActions: 'Quick Actions',
};

export const NAVBAR_VARIANTS: Record<Uppercase<NavbarVariant>, NavbarVariant> = {
  NOVA: 'nova',
  DEFAULT: 'default',
} as const;
